/**
 * Conversation manager using the existing AssistantApi.
 *
 * Supports URL-driven conversation selection: pass `initialConversationId`
 * (from search params) to restore a specific conversation on load.
 * The consumer is responsible for observing `currentConversationId` from the
 * return value and syncing it to the URL or any other external state.
 */
import { useState, useEffect, useCallback } from "react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type { Conversation } from "~/components/assistant/types";

interface UseConversationManagerProps {
  api: AssistantApi;
  datasetId: string;
  initialConversationId?: string | null;
}

export function useConversationManager({
  api,
  datasetId,
  initialConversationId,
}: UseConversationManagerProps) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [loadError, setLoadError] = useState<boolean>(false);

  /**
   * Create a new conversation
   */
  const createConversation = useCallback(async () => {
    if (isCreating) {
      return null;
    }

    try {
      setIsCreating(true);
      const newConvo = await api.createConversation(datasetId);

      setConversations((prev) => [newConvo, ...prev]);
      setCurrentConversationId(newConvo.id);

      return newConvo;
    } catch (error) {
      console.error("Failed to create conversation:", error);
      return null;
    } finally {
      setIsCreating(false);
    }
  }, [api, datasetId, isCreating]);

  /**
   * Delete a conversation. If it was the last one, auto-creates a replacement
   * so the UI is never left in an empty state.
   */
  const deleteConversation = useCallback(
    async (conversationId: string, event: React.MouseEvent) => {
      event.stopPropagation();

      try {
        await api.deleteConversation(conversationId);
        let prevList: Conversation[] = [];
        let filtered: Conversation[] = [];
        setConversations((prev) => {
          prevList = prev;
          filtered = prev.filter((c) => c.id !== conversationId);
          return filtered;
        });

        if (filtered.length === 0) {
          setIsCreating(true);
          try {
            const newConvo = await api.createConversation(datasetId);
            setConversations([newConvo]);
            setCurrentConversationId(newConvo.id);
          } catch (createError) {
            console.error("Failed to auto-create conversation after deletion:", createError);
            setCurrentConversationId(null);
          } finally {
            setIsCreating(false);
          }
        } else if (currentConversationId === conversationId) {
          const deletedIndex = prevList.findIndex((c) => c.id === conversationId);
          const nextIndex = Math.min(deletedIndex, filtered.length - 1);
          setCurrentConversationId(filtered[nextIndex].id);
        }
      } catch (error) {
        console.error("Failed to delete conversation:", error);
      }
    },
    [api, currentConversationId, datasetId]
  );

  /**
   * Rename a conversation
   */
  const renameConversation = useCallback(
    async (conversationId: string, displayName: string) => {
      try {
        await api.updateConversation(conversationId, { display_name: displayName });

        setConversations((prev) =>
          prev.map((c) => (c.id === conversationId ? { ...c, display_name: displayName } : c))
        );
      } catch (error) {
        console.error("Failed to rename conversation:", error);
      }
    },
    [api]
  );

  /**
   * Update a conversation's display name in local state only (no API call).
   * Used when the backend has already persisted the name (e.g. via a stream event).
   */
  const updateConversationName = useCallback((conversationId: string, displayName: string) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === conversationId ? { ...c, display_name: displayName } : c))
    );
  }, []);

  /**
   * Select a conversation
   */
  const selectConversation = useCallback((conversationId: string) => {
    setCurrentConversationId(conversationId);
  }, []);

  /**
   * Load all conversations for the dataset
   */
  useEffect(() => {
    if (!datasetId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(false);
        const convos = await api.listConversations(datasetId);

        if (cancelled) return;

        const sorted = convos.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        if (sorted.length === 0) {
          const newConvo = await api.createConversation(datasetId);
          if (cancelled) return;
          setConversations([newConvo]);
          setCurrentConversationId(newConvo.id);
          return;
        }

        setConversations(sorted);

        const targetId =
          initialConversationId && sorted.some((c) => c.id === initialConversationId)
            ? initialConversationId
            : (sorted[0]?.id ?? null);
        setCurrentConversationId(targetId);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load conversations:", error);
        setLoadError(true);
        setConversations([]);
        setCurrentConversationId(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialConversationId intentionally excluded; it's only for the first load
  }, [api, datasetId]);

  return {
    conversations,
    currentConversationId,
    isLoading,
    isCreating,
    loadError,
    createConversation,
    deleteConversation,
    renameConversation,
    updateConversationName,
    selectConversation,
  };
}
